import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from 'appHooks'

export type SelectedDocument = {
  fileId: string
  filename: string
}

type Filter = {
  isEditing?: boolean
  selectedDocuments?: SelectedDocument[]
  selectedUnitsToAdd?: string[]
  selectedLabelsToAdd: string[]
  selectAllOnPage: boolean
  currentPageDocumentIds: string[]
}

const initialState: Filter = {
  isEditing: false,
  selectedDocuments: [],
  selectedUnitsToAdd: [],
  selectedLabelsToAdd: [],
  selectAllOnPage: false,
  currentPageDocumentIds: [],
}

const boardUpdateListSlice = createSlice({
  name: 'boardUpdateList',
  initialState,
  reducers: {
    resetBoardUpdateList: (state) => {
      state.isEditing = initialState.isEditing
      state.selectedDocuments = initialState.selectedDocuments
      state.selectedUnitsToAdd = initialState.selectedUnitsToAdd
      state.selectedLabelsToAdd = initialState.selectedLabelsToAdd
      state.selectAllOnPage = initialState.selectAllOnPage
      state.currentPageDocumentIds = initialState.currentPageDocumentIds
    },
    setBoardUpdateList: (state, action: PayloadAction<Filter>) => {
      state.isEditing = action.payload.isEditing
      state.selectedDocuments = action.payload.selectedDocuments
      state.selectedUnitsToAdd = action.payload.selectedUnitsToAdd
      state.selectedLabelsToAdd = action.payload.selectedLabelsToAdd
    },
    setBoardIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload
    },
    setBoardSelectedDocument: (
      state,
      action: PayloadAction<SelectedDocument[]>
    ) => {
      state.selectedDocuments = action.payload
    },
    setBoardSelectedUnitsToAdd: (state, action: PayloadAction<string[]>) => {
      state.selectedUnitsToAdd = action.payload
    },
    setBoardSelectedLabelsToAdd: (state, action: PayloadAction<string[]>) => {
      state.selectedLabelsToAdd = action.payload
    },
    addDocument: (state, action: PayloadAction<SelectedDocument>) => {
      // Avoid duplicates
      if (
        !state.selectedDocuments.some(
          (doc) => doc.fileId === action.payload.fileId
        )
      ) {
        state.selectedDocuments = [...state.selectedDocuments, action.payload]
      }
    },
    addDocuments: (state, action: PayloadAction<SelectedDocument[]>) => {
      // Merge new documents with existing ones, avoiding duplicates
      const existingIds = new Set(
        state.selectedDocuments.map((doc) => doc.fileId)
      )
      const newDocuments = action.payload.filter(
        (doc) => !existingIds.has(doc.fileId)
      )
      state.selectedDocuments = [...state.selectedDocuments, ...newDocuments]
    },
    removeDocument: (state, action: PayloadAction<string>) => {
      state.selectedDocuments = state.selectedDocuments.filter(
        (doc) => doc.fileId !== action.payload
      )
    },
    setBoardSelectAllOnPage: (state, action: PayloadAction<boolean>) => {
      state.selectAllOnPage = action.payload
    },
    setCurrentPageDocumentIds: (state, action: PayloadAction<string[]>) => {
      state.currentPageDocumentIds = action.payload
    },
    removeCurrentPageDocuments: (state) => {
      state.selectedDocuments = state.selectedDocuments.filter(
        (doc) => !state.currentPageDocumentIds.includes(doc.fileId)
      )
    },
  },
})

export const {
  resetBoardUpdateList,
  setBoardUpdateList,
  setBoardIsEditing,
  setBoardSelectedDocument,
  setBoardSelectedUnitsToAdd,
  setBoardSelectedLabelsToAdd,
  addDocument,
  addDocuments,
  removeDocument,
  setBoardSelectAllOnPage,
  setCurrentPageDocumentIds,
  removeCurrentPageDocuments,
} = boardUpdateListSlice.actions

export default boardUpdateListSlice

export const getBoardUpdateList = (state: RootState) => state.boardUpdateList

export const getBoardUpdateListIsEditing = (state: RootState) =>
  state.boardUpdateList.isEditing

export const getBoardUpdateListSelectedDocument = (state: RootState) =>
  state.boardUpdateList.selectedDocuments

export const getBoardUpdateListSelectedUnitsToAdd = (state: RootState) =>
  state.boardUpdateList.selectedUnitsToAdd

export const getBoardUpdateListSelectedLabelsToAdd = (state: RootState) =>
  state.boardUpdateList.selectedLabelsToAdd

export const getBoardUpdateListSelectAllOnPage = (state: RootState) =>
  state.boardUpdateList.selectAllOnPage

export const getCurrentPageDocumentIds = (state: RootState) =>
  state.boardUpdateList.currentPageDocumentIds
